import { listMasters, listRecords } from "@/lib/api/client";
import { notFound } from "@/lib/notFound";
import { masterCodeOf, type MasterSummary } from "@/lib/api/types";
import type { RelatedRows } from "@/components/masters/RecordForm";

/** Find one configured master, 404ing when the sport doesn't declare it. */
export async function loadMaster(sport: string, masterCode: string): Promise<MasterSummary> {
  const masters = await listMasters(sport);
  const master = masters.find((m) => m.code === masterCode);
  if (master === undefined) notFound();
  return master;
}

/**
 * Fetch the rows of every master this one points at (parent, memberOf targets,
 * and ref fields), so the form can render real pickers instead of asking the
 * operator to type a code. Each target is fetched once even if referenced twice.
 */
export async function loadRelatedRows(
  sport: string,
  master: MasterSummary,
): Promise<RelatedRows> {
  const targets = new Set<string>();
  for (const relation of master.definition.relations ?? []) targets.add(relation.master);
  for (const field of master.definition.fields ?? []) {
    const code = masterCodeOf(field.target);
    if (code !== null) targets.add(code);
  }

  const entries = await Promise.all(
    [...targets].map(async (code) => {
      // Every option a dropdown could offer. Bounded rather than unbounded, so
      // a master that grows past this degrades into a picker missing entries
      // instead of a page that never loads — and that is the point at which it
      // needs to become a search box rather than a select.
      const page = await listRecords(sport, code, { limit: 500 });
      return [code, page.rows.map((r) => ({ code: r.code, name: r.name }))] as const;
    }),
  );
  return Object.fromEntries(entries);
}
